import { formatCnpj, sanitizeCnpj } from "./cnpj";
import type { JsonPrimitive } from "../types/json";

const abbreviations = new Map<string, string>([
  ["cnpj", "CNPJ"],
  ["cpf", "CPF"],
  ["cep", "CEP"],
  ["cnae", "CNAE"],
  ["uf", "UF"],
  ["id", "ID"],
  ["ibge", "IBGE"],
  ["siafi", "SIAFI"],
  ["iso2", "ISO 2"],
  ["iso3", "ISO 3"],
  ["mei", "MEI"],
  ["ddd", "DDD"],
  ["fax", "Fax"],
  ["email", "E-mail"],
]);

export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => {
      const normalized = word.toLowerCase();
      const abbreviation = abbreviations.get(normalized);
      return abbreviation ?? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
    })
    .join(" ");
}

export function formatCep(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : value;
}

export function formatPhone(ddd: string, phone: string): string {
  const digits = `${ddd}${phone}`.replace(/\D/g, "");
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return [ddd, phone].filter(Boolean).join(" ");
}

export function formatCurrency(value: string | number): string {
  const number = typeof value === "number" ? value : Number(value.replace(",", "."));
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  }).format(number);
}

export function formatDate(value: string): string {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "medium",
      timeZone: "UTC",
    }).format(date);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(date);
}

function looksLikeIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(?:T[\d:.+-]+Z?)?$/.test(value);
}

export function formatPrimitive(key: string, value: JsonPrimitive): string {
  if (value === null || value === "") return "Não informado";
  if (typeof value === "boolean") return value ? "Sim" : "Não";

  const normalizedKey = key.toLowerCase();
  const stringValue = String(value);

  if (normalizedKey.includes("cnpj") && sanitizeCnpj(stringValue).length === 14) {
    return formatCnpj(stringValue);
  }

  if (normalizedKey === "cep" || normalizedKey.endsWith("_cep")) {
    return formatCep(stringValue);
  }

  if (normalizedKey.includes("capital_social")) {
    return formatCurrency(value);
  }

  if (normalizedKey.startsWith("data_") || normalizedKey.endsWith("_em") || looksLikeIsoDate(stringValue)) {
    return formatDate(stringValue);
  }

  if (typeof value === "number" && !normalizedKey.endsWith("id") && normalizedKey !== "id") {
    return new Intl.NumberFormat("pt-BR").format(value);
  }

  return stringValue;
}
